import { AlertCircle, CheckCircle, Server, XCircle } from 'lucide-react';

import {
  AllClientsHealthResponse,
  IncidentStatus,
  ServiceStatus,
} from '@/types/service/serviceHealth';

import { Card, CardContent } from '@/components/ui/card';

interface ServiceHealthStatsProps {
  data: AllClientsHealthResponse;
  activeFilter: ServiceStatus | null;
  onFilterChange: (filter: ServiceStatus | null) => void;
}

const ServiceHealthStats = ({ data, activeFilter, onFilterChange }: ServiceHealthStatsProps) => {
  // Calculate summary statistics
  const totalClients = data.clients.length;
  const operationalCount = data.clients.filter(
    (c) => c.overall_status === ServiceStatus.OPERATIONAL,
  ).length;
  const degradedCount = data.clients.filter(
    (c) => c.overall_status === ServiceStatus.DEGRADED,
  ).length;
  const downCount = data.clients.filter((c) => c.overall_status === ServiceStatus.DOWN).length;

  // Count total ongoing incidents
  const totalOngoingIncidents = data.clients.reduce((total, client) => {
    return (
      total +
      client.environments.reduce((envTotal, env) => {
        return (
          envTotal + env.incidents.filter((inc) => inc.status === IncidentStatus.ONGOING).length
        );
      }, 0)
    );
  }, 0);

  const stats = [
    {
      label: 'Total Clients',
      value: totalClients,
      icon: Server,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      filter: null,
      clickable: true, // Make clickable to clear all filters
    },
    {
      label: 'Operational',
      value: operationalCount,
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      filter: ServiceStatus.OPERATIONAL,
      clickable: true, // Make clickable to filter operational
    },
    {
      label: 'Degraded',
      value: degradedCount,
      icon: AlertCircle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      filter: ServiceStatus.DEGRADED,
      clickable: true,
    },
    {
      label: 'Down',
      value: downCount,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      filter: ServiceStatus.DOWN,
      clickable: true,
    },
  ];

  return (
    <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
      {stats.map((stat) => {
        const Icon = stat.icon;
        const isActive = activeFilter === stat.filter;
        const isClickable = stat.clickable; // Always clickable if marked as clickable, regardless of value

        return (
          <Card
            key={stat.label}
            className={`${
              isClickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
            } ${isActive ? 'ring-2 ring-primary ring-offset-2' : ''}`}
            onClick={() => {
              if (isClickable) {
                onFilterChange(isActive ? null : stat.filter);
              }
            }}
          >
            <CardContent className='p-4'>
              <div className='flex items-center gap-3'>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                <div>
                  <p className='text-2xl font-bold'>{stat.value}</p>
                  <p className='text-sm text-muted-foreground'>{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Ongoing Incidents Card (full width on mobile, spanning 2 cols on desktop if needed) */}
      {totalOngoingIncidents > 0 && (
        <Card className='col-span-2 md:col-span-4'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-lg bg-orange-50'>
                <AlertCircle className='h-5 w-5 text-orange-600' />
              </div>
              <div>
                <p className='text-2xl font-bold text-orange-600'>{totalOngoingIncidents}</p>
                <p className='text-sm text-muted-foreground'>
                  Ongoing Incident{totalOngoingIncidents > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ServiceHealthStats;
